// extensionSaveShare.js — Save / Share-to-members actions for the /add-app
// extension builder (Phase 1 frontend slice, per Nika "save as extension and
// reuse/share to internal members").
//
// SAFETY GATE (per Nika "make it safe and secure"): SHARING an extension makes
// it visible + installable to EVERY workspace member, so it must be an explicit,
// human-confirmed action — never implicit. saveExtension (private) needs no
// confirm; shareExtension flips scope to 'shared' ONLY after the user confirms.
//
// Extensions are persisted through the AiService (sdk.ai.createExtension /
// updateExtension), the same store loadAddAppExtensions reads. We never write a
// shared extension without going through confirmShareExtension first.

// Bare shared-library import, not a deep aiService.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0). Deferred-call
// wrapper, not a module-scope destructure — see app.js's circular-init note.
import { shellGetAiService, shellSay } from './shellSharedDelegates.js'
import { activeWorkspaceId } from './scopedClient.js'

// A bundle sentence for a save/share notice or refusal: through the calling
// element's polyglot (every public export below is `el.call`ed, so the notice
// follows the session language), or the SHIPPED ENGLISH on a bare call — never a
// raw key. The notice is a toast that dismisses itself after 3.5 s, so it is
// resolved when it is PRODUCED, not stored as a key.

// The last thing rendered into the build pane — set by renderExtensionInPane so
// Save knows what to persist even before the cloud agent created a Project for
// it. Shape: { name, source, entities }.
export const setLastRenderedExtension = (info) => {
  if (typeof window === 'undefined') return
  window.__addAppLastRender =
    info && info.source
      ? { name: info.name || 'Extension', source: info.source, entities: info.entities || [] }
      : null
}

export const getLastRenderedExtension = () =>
  (typeof window !== 'undefined' && window.__addAppLastRender) || null

// Resolve the payload to persist: prefer the explicitly-passed extension, else
// the last-rendered one. Returns null when there's nothing to save.
const _resolvePayload = (ext) => {
  const e = ext && ext.source ? ext : getLastRenderedExtension()
  if (!e || !e.source) return null
  return { name: e.name || 'Extension', source: e.source, entities: e.entities || [] }
}

// The app the builder is bound to — a Project the build itself already stored
// (create_app / create_extension persist on every build) — or null. Its name is
// the one Your apps lists. A built MULTI-PAGE app leaves no single-page payload
// (buildInlineMultiPageDef clears it: a multi-page app has no one source), and
// neither does a sandboxed render, so a built app is known by this binding,
// not by the last single-page render (persona round 5, lina: Save on a built
// app said "Nothing to save — build an app first").
const _boundApp = (root) => {
  const id = root && root._addAppProjectId ? String(root._addAppProjectId) : ''
  if (!id) return null
  const apps = Array.isArray(root._userApps) ? root._userApps : []
  const row = apps.find((a) => a && String(a.id) === id)
  const name = row && typeof row.name === 'string' ? row.name.trim() : ''
  return { id, name }
}

// Persist the current extension at the given scope. `scope` is 'private' |
// 'shared'. When the active builder is already bound to a Project
// (root._addAppProjectId), UPDATE it; otherwise CREATE. Returns
// { ok, id, scope } / { ok:false, error }.
const _persist = async (root, scope, ext) => {
  const ai = shellGetAiService()
  if (
    !ai ||
    (typeof ai.createExtension !== 'function' && typeof ai.updateExtension !== 'function')
  ) {
    return { ok: false, error: 'workspace.addApp.saveError.storeUnavailable' }
  }
  const payload = _resolvePayload(ext)
  const bound = _boundApp(root)
  if (!payload && !bound) return { ok: false, error: 'workspace.addApp.saveError.nothingToSave' }

  const workspaceId = activeWorkspaceId(root)
  if (!workspaceId) return { ok: false, error: 'workspace.addApp.saveError.noWorkspace' }

  // A built app with no single-page payload (a multi-page app, a sandboxed
  // render): its pages are stored already, so Save / Share set its scope
  // only — the server's update takes a scope without a source and keeps the
  // stored pages (persona round 5, lina: "Nothing to save — build an app
  // first" over a built app).
  //
  // A save never moves an app OUT of the workspace (persona round 5, aiko:
  // Save on her stored app showed nothing): a bound app that is shared
  // already stays shared, nothing is written, and the line says where it is
  // (`kept`; saveExtension picks workspace.addApp.save.savedShared). A
  // private app's Save writes its scope; a Share moves only its place.
  if (!payload) {
    const row =
      (Array.isArray(root && root._userApps) &&
        root._userApps.find((a) => a && String(a.id) === bound.id)) ||
      null
    const place =
      ((row && row.scope) || (root && root._addAppScope)) === 'shared' ? 'shared' : 'private'
    if (scope !== 'shared' && place === 'shared') {
      return { ok: true, id: bound.id, scope: place, name: bound.name, kept: true }
    }
    if (typeof ai.updateExtension !== 'function') {
      return { ok: false, error: 'workspace.addApp.saveError.storeUnavailable' }
    }
    try {
      await ai.updateExtension(bound.id, { workspaceId, scope })
      if (root && root.update) root.update({ _addAppScope: scope })
      return { ok: true, id: bound.id, scope, name: bound.name }
    } catch (err) {
      return { ok: false, error: (err && err.message) || String(err) }
    }
  }

  const existingId = root && root._addAppProjectId ? String(root._addAppProjectId) : null
  const body = {
    workspaceId,
    name: payload.name,
    source: payload.source,
    entities: payload.entities,
    scope
  }
  try {
    let res
    if (existingId && typeof ai.updateExtension === 'function') {
      res = await ai.updateExtension(existingId, body)
    } else if (typeof ai.createExtension === 'function') {
      res = await ai.createExtension(body)
    } else {
      // No create method but we have an id-less extension — can't persist.
      return { ok: false, error: 'workspace.addApp.saveError.cannotCreate' }
    }
    const id = (res && (res.id || res._id)) || existingId || null
    // Re-bind the builder to the (possibly new) Project + refresh the sidebar so
    // the saved extension appears under the right Visibility scope. A NEW id
    // is a bind, not a switch: the chat holds the conversation that built it,
    // so it is marked hydrated first and hydrateAddAppThread leaves it on
    // screen (simoneAiTransport injectPageInApp has the same mark).
    if (root && root.update) {
      if (id && String(id) !== String(existingId || '')) root.__addAppHydratedThreadId = String(id)
      root.update({
        _addAppProjectId: id ? String(id) : root._addAppProjectId,
        _addAppScope: scope
      })
    }
    // The (possibly large) DOMQL source now lives in the persisted extension —
    // drop the transient copy off `window` so it isn't held for the page's life.
    setLastRenderedExtension(null)
    return { ok: true, id, scope, name: payload.name }
  } catch (err) {
    return { ok: false, error: (err && err.message) || String(err) }
  }
}

// One-shot auto-dismiss for the transient save/share toast. Armed when the
// notice is SET (below) — a single setTimeout, not an always-on poll — and
// re-armed on each new notice so the latest message gets the full dwell. A
// line that carries the way to open the app (functions/addAppSaved.js — BLD-28:
// a 3.5 s toast was missed, "no message shows") stays 10 s; its link leaves
// with it.
let _noticeDismissTimer = null
const _armNoticeDismiss = (root, message) => {
  if (typeof window === 'undefined' || !root || !root.update) return
  if (_noticeDismissTimer) clearTimeout(_noticeDismissTimer)
  const link = root._addAppSaveLink
  const linked = !!(link && link.notice === message)
  _noticeDismissTimer = window.setTimeout(
    () => {
      _noticeDismissTimer = null
      // Only clear if this same message is still showing (a newer notice owns it).
      if (root._addAppSaveNotice === message) {
        root.update({ _addAppSaveNotice: null, _addAppSaveLink: null })
      }
    },
    linked ? 10000 : 3500
  )
}

// Shared post-persist UI hand-off: toast the outcome (+ any extra state patch on
// success), arm the auto-dismiss, and refresh the apps sidebar. Used by both
// save + confirm-share so the notice/refresh flow lives in one place.
const _notifyPersistResult = (thisEl, root, res, successKey, extra = {}) => {
  if (!root || !root.update) return
  const message = res.ok
    ? shellSay(thisEl, successKey)
    : shellSay(thisEl, 'workspace.addApp.save.failed').replace('{reason}', () => {
        // a refusal of our own is a bundle key; the server's is its own text,
        // shown as it is (never looked up as a key)
        const reason = String(res.error || '')
        return /^workspace\.[A-Za-z0-9.]+$/.test(reason) ? shellSay(thisEl, reason) : reason
      })
  if (res.ok && Object.keys(extra).length) root.update(extra)
  const app = res.ok && res.id ? { id: res.id, name: res.name, scope: res.scope } : null
  if (app) {
    // the app stands in Your apps now, under its own place (BLD-28)
    try {
      thisEl.call('addAppSavedRow', root, app)
    } catch (_) {}
  }
  let linked = false
  try {
    linked = !!thisEl.call('addAppSavedNotice', root, message, app)
  } catch (_) {
    linked = false
  }
  if (!linked && root._addAppSaveNotice !== message) {
    root.update({ _addAppSaveNotice: message, _addAppSaveLink: null })
  }
  _armNoticeDismiss(root, message)
  // A refusal also takes the kit's error toast (the shell's openNotification,
  // the one every Save in the workspace raises on a failure): the builder's
  // line leaves in 3.5 s, and a failed save must say why where the person
  // looks (W16b live check: two Save presses, no visible answer at all).
  if (!res.ok) {
    try {
      thisEl.call('openNotification', { title: message, type: 'error' })
    } catch (_) {
      /* the builder's line above still says it */
    }
  }
  try {
    thisEl.call('loadAddAppExtensions', root)
  } catch (_) {}
}

// PUBLIC — Save the current extension PRIVATELY (creator-only). No confirm.
// `this` is the calling element (el.call('saveExtension')).
export async function saveExtension(ext) {
  const root = (this && this.state && (this.state.root || this.state)) || null
  const res = await _persist(root, 'private', ext)
  // a stored app that stays shared says where it is, not "private apps"
  const key =
    res && res.kept && res.scope === 'shared'
      ? 'workspace.addApp.save.savedShared'
      : 'workspace.addApp.save.savedPrivate'
  _notifyPersistResult(this, root, res, key)
  return res
}

// PUBLIC — "Save as app": promote a TRANSIENT drawn answer view
// (render_answer_ui → root._aiAnswerPages['/answer/…']) into a persisted
// /add-app extension. The bridge between the two "workspace UI" modes: a view
// Simone drew on the fly becomes an app that lives in the workspace. Called from
// the PageSlot "Save as app" header button with the answer route.
//   1. recover the exact DOMQL source stashed on the answer def (metadata.__source),
//   2. createExtension (member Save — no agent turn),
//   3. queue the render + open /add-app so the new app lands visibly there.
export async function saveAnswerViewAsApp(route) {
  const root = (this && this.state && (this.state.root || this.state)) || null
  if (!root || !root.update) return { ok: false, error: 'no_root' }
  const r = String(route || root._canvasSlot || '')
  const def = (root._aiAnswerPages && root._aiAnswerPages[r]) || null
  const source = def && def.metadata && def.metadata.__source
  const name = (def && def.metadata && def.metadata.title) || 'Saved view'
  if (!source) {
    const nothingHere = shellSay(this, 'workspace.addApp.save.nothingHere')
    root.update({ _addAppSaveNotice: nothingHere })
    _armNoticeDismiss(root, nothingHere)
    return { ok: false, error: 'no_source' }
  }

  const ai = shellGetAiService()
  const workspaceId = activeWorkspaceId(root)
  if (!ai || typeof ai.createExtension !== 'function' || !workspaceId) {
    const unavailable = shellSay(this, 'workspace.addApp.save.failed').replace('{reason}', () =>
      shellSay(this, 'workspace.addApp.saveError.storeUnavailable')
    )
    root.update({ _addAppSaveNotice: unavailable })
    _armNoticeDismiss(root, unavailable)
    return { ok: false, error: 'store_unavailable' }
  }

  try {
    const res = await ai.createExtension({ workspaceId, name, source, scope: 'private' })
    const id = (res && (res.projectId || res.id || res._id)) || null
    // Queue the render + navigate to /add-app. Cross-page nav destroys and
    // re-creates the addApp page, so AppRender.onCreate re-fires and flushes this
    // queue (it polls the iframe to readiness before rendering — the flush used
    // to fire against an unconnected contentWindow and silently no-op, which is
    // why a saved app only landed in the sidebar). Bind the builder to the new
    // Project + close the transient answer view.
    if (typeof window !== 'undefined') window.__addAppPendingRender = { name, source, entities: [] }
    const savedAsApp = shellSay(this, 'workspace.addApp.save.savedAsApp').replace(
      '{name}',
      () => name
    )
    root.update({
      _addAppProjectId: id ? String(id) : null,
      _addAppRendered: false,
      _canvasSlot: null,
      _canvasSlotSecond: null,
      _slotFullscreen: null,
      _addAppSaveNotice: savedAsApp,
      _addAppSaveLink: null
    })
    // the new app stands in Your apps now, and the line opens it (BLD-28)
    if (id) {
      try {
        this.call('addAppSavedRow', root, { id, name, scope: 'private' })
        this.call('addAppSavedNotice', root, savedAsApp, { id, name, scope: 'private' })
      } catch (_) {}
    }
    _armNoticeDismiss(root, savedAsApp)
    try {
      this.call('openAppPage', '/add-app')
    } catch (_) {}
    try {
      this.call('loadAddAppExtensions', root)
    } catch (_) {}
    return { ok: true, id }
  } catch (err) {
    const msg = (err && err.message) || String(err)
    const failedNote = shellSay(this, 'workspace.addApp.save.failed').replace('{reason}', () => msg)
    root.update({ _addAppSaveNotice: failedNote })
    _armNoticeDismiss(root, failedNote)
    return { ok: false, error: msg }
  }
}

// PUBLIC — open the share confirmation. Does NOT share yet: the kit's
// confirmDialog asks, and confirmShareExtension flips the scope only when the
// person confirms. The kit's dialog owns the dialog contract — the focus moves
// into it on open, Tab stays inside, Escape and "Keep private" close it, and
// the focus goes back to the Share button (W16b live check: the builder's own
// overlay left the focus behind it and then on the page body).
export function requestShareExtension() {
  const root = (this && this.state && (this.state.root || this.state)) || null
  if (!root || !root.update) return { ok: false }
  const payload = _resolvePayload()
  const bound = payload ? null : _boundApp(root)
  if (!payload && !bound) {
    const buildFirst = shellSay(this, 'workspace.addApp.save.buildFirst')
    root.update({ _addAppSaveNotice: buildFirst })
    _armNoticeDismiss(root, buildFirst)
    return { ok: false, error: 'nothing_to_share' }
  }
  // a built app known by its binding: the name Your apps lists, else the
  // share confirm's own words for an app without one ("This app")
  const name = payload
    ? payload.name
    : bound.name || shellSay(this, 'workspace.addApp.share.thisExtension')
  const el = this
  el.call('confirmDialog', {
    title: shellSay(el, 'workspace.addApp.share.title'),
    message: shellSay(el, 'workspace.addApp.share.body').replace('{name}', () => name),
    cancelText: shellSay(el, 'workspace.addApp.share.keepPrivate'),
    confirmText: shellSay(el, 'workspace.addApp.share.confirm'),
    // the title and the button say "share": no glyph beside the dialog's Close
    icon: null,
    onConfirm: () => el.call('confirmShareExtension')
  })
  return { ok: true, name }
}

// PUBLIC — the user CONFIRMED sharing. Flip scope to 'shared' (visible +
// installable to every workspace member). The kit's dialog has closed itself.
export async function confirmShareExtension(ext) {
  const root = (this && this.state && (this.state.root || this.state)) || null
  const res = await _persist(root, 'shared', ext)
  // toast + sidebar refresh via the shared helper (success message; failures
  // fall back to the helper's "Save failed")
  _notifyPersistResult(this, root, res, 'workspace.addApp.save.sharedAll')
  return res
}

// PUBLIC — the builder's Save press (`this` is the Save button, whose own
// state holds { saving, savedId }). The button says what the press did (W16b
// live check: two presses wrote the app — PATCH 200 — and the only answer was
// a 3.5 s line the person never saw): "Saving" while the write runs, "Saved"
// for the app it saved until the builder holds another one, and back to
// "Save" on a refusal, whose reason the kit's error toast and the builder's
// line carry (_notifyPersistResult). → the save's result, or null.
export async function addAppSavePress() {
  const el = this
  const s = el && el.state
  if (!s || typeof s.update !== 'function' || s.saving) return null
  s.update({ saving: true, savedId: '' })
  let res = null
  try {
    res = await el.call('saveExtension')
  } catch (_) {
    res = null
  }
  s.update({ saving: false, savedId: res && res.ok && res.id ? String(res.id) : '' })
  return res
}

// PUBLIC — the Save button's state → 'saving' | 'saved' | 'idle'. "Saved" is
// for the app the builder holds now (root._addAppProjectId): another app opened
// in the builder reads "Save" again. Every value is read on every run, so a
// reactive factory that calls it tracks all three (FA515).
export function addAppSaveStatus(s) {
  const saving = !!(s && s.saving)
  const savedId = String((s && s.savedId) || '')
  const bound = String((s && s.root && s.root._addAppProjectId) || '')
  if (saving) return 'saving'
  return savedId && savedId === bound ? 'saved' : 'idle'
}
